import Fuse from 'fuse.js';
import { useMemo, useState } from 'react';
import useGoogleMaps from '@/hooks/useGoogleMaps';
import LocatorFilters from './LocatorFilters';
import LocatorMap from './LocatorMap';
import LocatorMobileFilters from './LocatorMobileFilters';
import LocatorSearch from './LocatorSearch';
import { type FilterOption, type Station } from './Locator.types';

export type LocatorProps = {
  filterOptions: FilterOption;
  stations: Station[];
  apiKey: string;
  title: string;
};

const stripAccents = (str) => {
  if (typeof str !== 'string') {
    return str;
  }

  if (typeof String.prototype.normalize !== 'undefined') {
    return str.normalize('NFD').replace(/[\u0300-\u036F]/g, '');
  }

  return str;
};

const Locator: React.FC<LocatorProps> = ({
  filterOptions,
  stations,
  apiKey,
  title,
}) => {
  const loadFilterFromQueryString = (query, defaultValue = null) => {
    if ('URLSearchParams' in window) {
      const searchParams = new URLSearchParams(window.location.search);
      let filters = '';

      if (searchParams.has(query)) {
        filters = searchParams.get(query);

        if (typeof defaultValue === 'string') {
          return filters;
        }
        // we have a comma seperated list, , so split it
        return filters.split('__');
      }
    }

    return defaultValue;
  };
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState({
    services: loadFilterFromQueryString('s', []),
    type: loadFilterFromQueryString('t', ''),
    fuel: loadFilterFromQueryString('f', ''),
  });

  const [searchCompact, setSearchCompact] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentStation, setCurrentStation] = useState();
  const [currentDuration, setCurrentDuration] = useState(null);
  const [coords, setCoords] = useState({ latitude: null, longitude: null });

  const { isLoaded: isGoogleMapsLoaded, googleMaps } = useGoogleMaps(apiKey);

  // Regenerate the list of filtered stations only when the filters, query, or list of stations changes.
  const filteredStations = useMemo(() => {
    // setup fuse configuration
    const fuse = new Fuse(stations, {
      keys: [
        'location.address',
        'name',
        'location.suburb',
        'location.province',
        'location.city',
        'location.region',
        'fuels.name',
        'services.name',
      ],
      threshold: 0.29,
      getFn: (obj, path) => stripAccents(Fuse.config.getFn(obj, path)),
    });

    const { fuel, services, type } = filters;

    // perform the fuse search and then extract the item (station) from each
    // result if we have a query
    const filtered = !query
      ? stations
      : fuse.search(`'${stripAccents(query)}`).map((result) => result.item);

    // then run the filters across each of the items
    return filtered.filter((station) => {
      // check if the station has all the selected services
      const hasAllServices =
        services.length > 0
          ? services.every(
              (service) =>
                station.services.find((s) => s.code === service) !== undefined,
            )
          : true;

      // check if the station has the selected fuel type
      const hasFuel = fuel
        ? station.fuels.find((f) => f.slug === fuel) !== undefined
        : true;

      return hasAllServices && (!type || station.type_slug === type) && hasFuel;
    });
  }, [query, stations, filters]);

  const handleSelect = (id) => {
    // set current station
    setCurrentStation(id);

    // reset duration
    setCurrentDuration(undefined);

    // find the station details for selected station
    const station = stations.find((s) => s.site_id === id);

    if (!station) {
      // unlikely situation, but just exit early here if so
      return;
    }

    const { latitude, longitude } = coords;

    // need current coords to proceed
    if (!latitude || !longitude) {
      return;
    }

    // make api request to determine travel time
    const service = new googleMaps.DirectionsService();

    service.route(
      {
        origin: { lat: latitude, lng: longitude },
        destination: {
          lat: station.location.latitude,
          lng: station.location.longitude,
        },
        travelMode: 'DRIVING',
      },
      (response, status) => {
        if (status === 'OK') {
          const { routes } = response;

          // we aren't using the provideRouteAlternatives flag so we can extract
          // just the first route
          const route = routes?.[0];

          // since we know we have no waypoints and 1 destination we can just
          // extract the first leg
          const leg = route?.legs?.[0];

          const duration = leg?.duration?.text;

          // just in case we don't get the expected key from the API
          if (!duration) {
            return;
          }

          setCurrentDuration(`${duration} away`);
        }
      },
    );
  };

  return (
    <div>
      <div className="page-header page-header--locator">
        <div className="page-header__text container">
          <h1 className="page-header__title" data-testid="PageHeader__Title">
            {title}
          </h1>

          <LocatorSearch
            isCompact={searchCompact}
            isGoogleMapsLoaded={isGoogleMapsLoaded}
            googleMaps={googleMaps}
            onSearch={(query) => {
              setCurrentStation(null);

              // if query starts with 'Z ' then strip it off as that matches everything
              if (query.startsWith('Z ')) {
                query = query.slice(2);
              }

              setQuery(query);
              setSearchCompact(true);
            }}
            onUpdateCoords={(updated) => setCoords(updated)}
          />
        </div>
      </div>
      <LocatorFilters
        active={showFilters}
        filterOptions={filterOptions}
        filters={filters}
        onBack={() => setShowFilters(false)}
        onFilter={(updated) => {
          setFilters({ ...filters, ...updated });
          setSearchCompact(true);
        }}
      />
      <LocatorMobileFilters
        filterOptions={filterOptions}
        filters={filters}
        onFiltersClick={() => setShowFilters(true)}
        onClearFilter={(filter, value) =>
          setFilters({ ...filters, [filter.toString()]: value })
        }
      />
      <LocatorMap
        currentDuration={currentDuration}
        currentStation={currentStation}
        googleMaps={googleMaps}
        onSelect={handleSelect}
        stations={filteredStations}
      />
    </div>
  );
};

export default Locator;
